/**
 * Replay Lab run detail (Phase 1b) — original vs replay transcript
 * side-by-side, red/green check chips (advisory = amber, see CheckChip),
 * and the per-turn agenda projection captured by the runner.
 * Route: /replay-lab/runs/:runId
 */
import { useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'

import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { Spinner } from '@/components/ui'
import { useCanAccessFeature, useFeature } from '@/hooks'
import { useReplayRun, useReplayScenarios } from '@/hooks/useReplayLab'
import type { ReplayTranscriptEntry } from '@/types/replayLab'
import type { SimulateAgendaState } from '@/types/workflowSimulate'
import { cn } from '@/utils'
import { CheckChip } from './CheckChip'
import { RunStatusBadge } from './RunStatusBadge'
import { useOriginalConversation } from './useOriginalConversation'

/** Button-based disclosure (NOT native <details> — happy-dom's summary-click
 * toggle is unreliable, and a controlled button tests deterministically). */
function AgendaDisclosure({ agenda, turn }: { agenda: SimulateAgendaState; turn: number }) {
  const [open, setOpen] = useState(false)
  const filled = agenda.slots.filter((s) => s.filled).length
  return (
    <div className="rounded-sm border border-border px-2 py-1 bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[11px] text-muted-foreground hover:text-foreground"
      >
        Agenda after turn {turn} — {agenda.stage ?? 'no stage'} · {filled}/{agenda.slots.length} slots filled
      </button>
      {open && (
        <ul className="mt-1 space-y-0.5">
          {agenda.slots.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5 font-mono text-[11px]">
              <span aria-hidden className={s.filled ? 'text-success' : 'text-muted-foreground'}>
                {s.filled ? '●' : '○'}
              </span>
              <span className="text-foreground">{s.key}</span>
              {s.required_now && !s.filled && (
                <span className="text-warning-strong">required now</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Bubble({ side, tone, children }: {
  side: 'left' | 'right'
  tone: 'muted' | 'primary' | 'info'
  children: ReactNode
}) {
  return (
    <div className={cn('flex', side === 'left' ? 'justify-start' : 'justify-end')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-3 py-2 text-sm text-foreground whitespace-pre-wrap',
          tone === 'muted' && 'bg-muted',
          tone === 'primary' && 'bg-primary/10',
          tone === 'info' && 'bg-info/10',
        )}
      >
        {children}
      </div>
    </div>
  )
}

function fingerprintLabel(fp: Record<string, unknown>): string {
  const parts: string[] = []
  if (fp.bundle_version != null) parts.push(`bundle v${fp.bundle_version}`)
  if (fp.workflow_id != null) {
    parts.push(`${fp.workflow_id}${fp.workflow_version != null ? ` v${fp.workflow_version}` : ''}`)
  }
  if (fp.extraction_transport != null) parts.push(`extraction: ${fp.extraction_transport}`)
  return parts.join(' · ')
}

const RUN_STALL_HINT_MS = 120_000

export function ReplayRunPage() {
  const { runId } = useParams<{ runId: string }>()
  const canAccess = useCanAccessFeature('replay_lab', 'workflows:configure')
  const inboxEnabled = useFeature('inbox')
  const run = useReplayRun(canAccess ? runId : undefined)
  const scenarios = useReplayScenarios(canAccess)
  const scenario =
    scenarios.data?.find((s) => s.id === run.data?.scenarioId) ?? null
  const original = useOriginalConversation(
    scenario?.sourceConversationId,
    canAccess && inboxEnabled,
  )

  if (!canAccess) {
    return (
      <PageFrame width="form" className="py-8">
        <p className="text-sm text-muted-foreground">
          You don't have access to Replay Lab.
        </p>
      </PageFrame>
    )
  }
  if (run.isLoading) {
    return <p className="py-8 text-sm text-muted-foreground">Loading run…</p>
  }
  if (run.isError || !run.data) {
    return (
      <PageFrame width="form" className="py-8">
        <p className="text-sm text-muted-foreground">
          Couldn't load this run.{' '}
          <Link className="underline" to="/replay-lab">Back to Replay Lab</Link>
        </p>
      </PageFrame>
    )
  }

  const detail = run.data

  if (detail.status === 'running') {
    const ageMs = Date.now() - new Date(detail.createdAt).getTime()
    return (
      <PageFrame width="detail" className="space-y-4">
        <PageHeader
          title={scenario?.name ?? 'Replay run'}
          sticky
          back={{ to: '/replay-lab', label: 'Replay Lab' }}
          tag={<RunStatusBadge status={detail.status} />}
        />
        <div
          role="status"
          className="rounded-sm border border-info/30 bg-info/10 px-3 py-3 text-sm text-info flex items-center gap-2"
        >
          <Spinner size="sm" aria-hidden="true" />
          Replaying through the real engine — this can take a minute or two.
        </div>
        {ageMs > RUN_STALL_HINT_MS && (
          <p className="text-xs text-muted-foreground">
            This is taking longer than expected — if it doesn't finish, you can
            re-run it from{' '}
            <Link className="underline" to="/replay-lab">Replay Lab</Link>.
          </p>
        )}
      </PageFrame>
    )
  }

  const results = detail.checks.results ?? []
  const hasFailedAdvisory = results.some((r) => r.advisory && !r.passed)
  // agenda_trace carries transcript + agenda; fall back to the bare
  // transcript if a run predates the trace (defensive — same runner writes both).
  const turns: (ReplayTranscriptEntry & { agenda: SimulateAgendaState | null })[] =
    detail.agendaTrace.length > 0
      ? detail.agendaTrace
      : detail.transcript.map((t) => ({ ...t, agenda: null }))

  return (
    <PageFrame width="detail" className="space-y-4">
      <PageHeader
        title={scenario?.name ?? 'Replay run'}
        sticky
        back={{ to: '/replay-lab', label: 'Replay Lab' }}
        tag={<RunStatusBadge status={detail.status} />}
        subtitle={
          <p className="font-mono text-xs">
            {detail.mode} · {new Date(detail.createdAt).toLocaleString()}
            {fingerprintLabel(detail.configFingerprint) &&
              ` · ${fingerprintLabel(detail.configFingerprint)}`}
          </p>
        }
      />

      {detail.status === 'error' && (
        <div
          role="alert"
          className="rounded-sm border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
        >
          <p className="font-medium">Replay crashed: {detail.error}</p>
          <p className="mt-1 text-muted-foreground">
            The replay conversation was kept for inspection (error runs are not
            cleaned up).
          </p>
        </div>
      )}

      {results.length > 0 && (
        <section aria-label="Checks" className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {results.map((r) => (
              <CheckChip key={r.check} result={r} />
            ))}
          </div>
          {hasFailedAdvisory && (
            <p className="text-xs text-muted-foreground">
              Advisory checks report but don't gate the verdict — a replay can't reproduce
              these deterministically: order totals and paid outcomes need the payment
              webhook (never delivered to a sandboxed replay), and the delivery-inclusive
              checkout total uses a fixed sandbox courier fee.
            </p>
          )}
          <ul className="space-y-0.5">
            {results.filter((r) => !r.passed).map((r) => (
              <li key={r.check} className="text-xs text-muted-foreground">
                <span className="font-mono text-foreground">{r.check}</span>: {r.detail}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section
          aria-label="Original conversation"
          className="rounded-sm border border-border flex flex-col"
        >
          <header className="px-3 py-2 border-b border-border text-sm font-medium text-foreground">
            Original conversation
          </header>
          <div className="max-h-[65vh] overflow-y-auto p-3 space-y-2">
            {original.isSuccess ? (
              original.data.messages.map((m) => {
                const isCustomer = m.senderType === 'customer' || m.role === 'user'
                const isBusiness = m.senderType === 'business'
                return (
                  <Bubble
                    key={m.id}
                    side={isCustomer ? 'left' : 'right'}
                    tone={isCustomer ? 'muted' : isBusiness ? 'info' : 'primary'}
                  >
                    {m.content}
                  </Bubble>
                )
              })
            ) : scenarios.isLoading || original.isFetching ? (
              <Spinner size="sm" />
            ) : (
              <p className="text-xs text-muted-foreground">
                Original conversation is unavailable (deleted, or the inbox
                feature is off). The replay pane's customer turns are the
                captured{scenario?.anonymized ? ' (anonymized)' : ''} script.
              </p>
            )}
          </div>
        </section>

        <section aria-label="Replay" className="rounded-sm border border-border flex flex-col">
          <header className="px-3 py-2 border-b border-border text-sm font-medium text-foreground">
            Replay
          </header>
          <div className="max-h-[65vh] overflow-y-auto p-3 space-y-3">
            {turns.length === 0 && (
              <p className="text-xs text-muted-foreground">No turns recorded.</p>
            )}
            {turns.map((t, i) => (
              <div key={i} className="space-y-1">
                <Bubble side="left" tone="muted">{t.customer}</Bubble>
                <Bubble side="right" tone="primary">{t.assistant}</Bubble>
                {t.agenda && <AgendaDisclosure agenda={t.agenda} turn={i + 1} />}
              </div>
            ))}
          </div>
        </section>
      </div>
    </PageFrame>
  )
}
